<template>
	<div
		class="lineage-node-wrapper"
		:child="d.isChildNode"
		:type="d.type"
		:root="d.root"
		:style="styleObj"
		xmlns="http://www.w3.org/1999/xhtml"
	>
		<Container :gap="4" :padding="0" align="left stretch" :grow="1">
			<div class="node-i-wrapper node-icon">
				<div class="node-icon-container">
					<Icon :name="d.icon ? d.icon : 'i-table'" type="filled" size="small" :effects="false" />
				</div>
			</div>
			<Container direction="column" align="left top" :gap="2" padding="0px 0px 0px 8px">
				<Container :gap="8" :padding="0">
					<div v-if="duplicateAlphabet" class="duplicate-indicator">
						{{ duplicateAlphabet }}
					</div>
					<Typography :type="d.isChildNode ? 'p2' : 'p1'" color="dark">
						{{ d.name }}
					</Typography>

					<div class="node-i-wrapper start">
						<div class="node-icon-container">
							<Icon v-if="d.state === 'verified'" size="small" name="i-tick-double" type="filled" />
						</div>
					</div>
				</Container>
				<Typography type="p2" :color="d.root ? 'default' : 'light'" overflow="ellipsis">
					{{ d.description }}
				</Typography>
			</Container>
			<Container
				v-if="d.features"
				:padding="0"
				:gap="12"
				align="right center"
				:grow="1"
				overflow="auto"
			>
				<div v-for="f in d.features" :key="f" class="node-i-wrapper start">
					<div class="node-icon-container">
						<Icon :name="f" type="filled" state="primary" size="20px" />
					</div>
				</div>
			</Container>
		</Container>
		<div class="node-i-wrapper end margin-lt-auto">
			<div class="node-icon-container">
				<Icon
					v-if="d.hasChildren"
					:name="d.childCollpsed ? 'i-chevron-down' : 'i-chevron-up'"
					class="toggle-child"
					type="filled"
					size="x-small"
					:effects="!d.root"
				/>
			</div>
		</div>
		<div class="node-info-wrapper">
			<div v-if="tag" class="node-i-wrapper start node-tag">
				<div class="node-icon-container">
					<Tag
						:size="tag.size ? tag.size : 'small'"
						:background="tag.background"
						:color="tag.color"
					>
						<Typography
							type="p3"
							weight="bold"
							transform="uppercase"
							:color="tag.color ? 'inherit' : 'black'"
						>
							{{ tag.text }}
						</Typography>
					</Tag>
				</div>
			</div>
			<div v-if="d.root" class="node-i-wrapper start">
				<div class="node-icon-container">
					<Icon name="i-location" type="filled" state="primary" :effects="false" />
				</div>
			</div>
			<div v-if="d.type === 'disabled'" class="node-i-wrapper start">
				<div class="node-icon-container" style="height: 16px">
					<Icon name="i-stop" type="filled" size="small" :effects="false" />
				</div>
			</div>
			<div v-if="subTag" class="node-i-wrapper end">
				<div class="node-icon-container">
					<Tag
						class="margin-lt-auto"
						:size="subTag.size ? subTag.size : 'small'"
						:background="subTag.background"
						:color="subTag.color"
					>
						<Typography
							type="p3"
							weight="bold"
							transform="uppercase"
							:color="subTag.color ? 'inherit' : 'black'"
						>
							{{ subTag.text }}
						</Typography>
					</Tag>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Typography from "./../../../Typography/Typography.vue";
import Icon from "./../../../Icon/Icon.vue";
import Container from "./../../../Container/Container.vue";
import Tag from "./../../../Tag/Tag.vue";

export default defineComponent({
	name: "Node",
	components: {
		Typography,
		Icon,
		Container,
		Tag
	},
	props: {
		d: {
			type: Object,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			default: () => {}
		},
		dupnames: {
			type: Array,
			default: () => []
		},
		size: {
			type: Object,
			default: () => {
				return {
					width: 36,
					height: 36
				};
			}
		}
	},
	computed: {
		styleObj() {
			return {
				width: `${this.size.width}px`,
				height: `${this.size.height}px`
			};
		},
		tag() {
			if (this.d.tag && typeof this.d.tag === "string") {
				if (this.d.tag.toLowerCase() === "s") {
					return { text: this.d.tag, background: "var(--secondary-200)" };
				} else if (this.d.tag.toLowerCase() === "t") {
					return { text: this.d.tag, background: "var(--color6-100)" };
				} else if (this.d.tag.toLowerCase() === "d") {
					return { text: this.d.tag, background: "var(--color5-200)" };
				} else {
					return { text: this.d.tag, background: null };
				}
			} else if (this.d.tag && typeof this.d.tag === "object") {
				return this.d.tag;
			}

			return null;
		},
		subTag() {
			if (this.d.subTag && typeof this.d.subTag === "string") {
				return { text: this.d.subTag, background: "var(--color3-100)" };
			} else if (this.d.subTag && typeof this.d.subTag === "object") {
				return this.d.subTag;
			}
			return null;
		},
		duplicateAlphabet() {
			const allAlpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789";

			const idx = this.dupnames.indexOf(this.d.name);

			return idx === -1 ? null : allAlpha.charAt(idx);
		}
	}
});
</script>
<style lang="scss">
div.lineage-node-wrapper {
	display: flex;
	border-radius: 4px;
	background-color: var(--dashboard-input);
	padding: 8px 12px 8px 8px;

	align-items: flex-start;
	justify-content: space-between;
	cursor: pointer;

	&[child] {
		background-color: var(--dashboard-element-light);
		align-items: center;
	}
	&[root] {
		// box-shadow: inset 0 0 0 2px var(--dashboard-primary);
		background-color: var(--primary-300);
	}
	// .toggle-child {
	// 	align-self: center;

	// 	margin-left: 12px;
	// }
	.node-info-wrapper {
		position: absolute;
		left: 0px;
		right: 0px;
		bottom: calc(100% + 14px);
		display: flex;
		align-items: flex-end;
		filter: opacity(1);

		> * + *:not(.margin-lt-auto) {
			margin-left: 8px;
		}
		.node-tag {
			width: 24px;
			max-width: 24px;
		}
	}

	&[type="disabled"] {
		background-color: transparent;
		pointer-events: none;
		border: 2px dashed var(--dashboard-font-light);
		// filter: opacity(0.5);
		// opacity: 0.5;
	}

	&:hover {
		box-shadow: inset 0 0 0 2px var(--dashboard-primary);
	}
	.node-icon-container {
		position: fixed;
		display: flex;
		justify-content: center;
		align-items: center;
	}
	.node-i-wrapper {
		display: inline-flex;
		align-items: center;
		height: 100%;
		// align-self: stretch;
		flex: 1 0 auto;
		&.end {
			justify-content: flex-end;
		}
		&.start {
			justify-content: flex-start;
		}
		&.center {
			justify-content: center;
		}
	}
	div.flow-tag {
		position: unset !important;
	}
	.node-icon {
		width: 16px;
		flex: 0 0 auto;
		margin-top: 8px;
	}
	.width-16 {
		width: 16px;
	}
}
</style>
